import { Link, useNavigate } from 'react-router-dom';
import React from 'react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../authentication/authSlice';
import { useDeleteEventMutation } from '../../../../api/eventsApiSlice';
import { useLazyGetEventQuery } from '../../../../api/eventsApiSlice';
import { selectCurrentAccessToken } from '../../../authentication/authSlice';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Chip, Avatar, ListItem, ListItemAvatar, ListItemText, IconButton, Alert, Button, Grid, Box, useMediaQuery } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAddNewLokiMutation } from '../../../../api/lokiSlice';

const itemStyles = {
    backgroundColor: 'taphaWhite.main',
    border: '1px solid transparent',
    borderLeft: '7px solid transparent',
    borderRadius: 5,
    contain: 'layout paint',
    transform: 'translateZ(0)',
    transition: 'transform 0.12s ease-out, background-color 0.12s ease-out, border-color 0.12s ease-out',
    '&:hover': {
        borderColor: 'taphaGreen.main',
        backgroundColor: 'grey.50',
        transform: 'translateX(2px)',
        cursor: 'pointer'
    }
};

const EventListItem = ({ event, events, deleteEvent }) => {
    const navigate = useNavigate();
    const screenIsBiggerThan600px = useMediaQuery('(min-width: 600px)');
    const bannerImage = event?.kuvat?.find(img => img.bannerImage === true);
    const user = useSelector(selectCurrentUser);
    const name = user.name;
    const [sendLog] = useAddNewLokiMutation();
    const accessToken = useSelector(selectCurrentAccessToken);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [copying, setCopying] = useState(false);
    const [fetchFullEvent] = useLazyGetEventQuery();

    const handleCopy = async () => {
        setCopying(true);

        try {
            const fullEvent = await fetchFullEvent({ id: event.id }, true).unwrap();
            const copiedEvent = { ...fullEvent, raportti: null, status: 'luonnos' };
            sessionStorage.setItem('copiedSessionValues', JSON.stringify(copiedEvent));
            navigate('/tapahtumat/uusi');
        } catch (err) {
            const fallbackCopiedEvent = { ...event, raportti: null, status: 'luonnos' };
            sessionStorage.setItem('copiedSessionValues', JSON.stringify(fallbackCopiedEvent));
            navigate('/tapahtumat/uusi');
        } finally {
            setCopying(false);
        }
    };

    const handleSuccess = (message) => {
        setSuccessMessage(message);
    };

    const handleError = (message) => {
        setErrorMessage(message);
    };

    const handleDeleteConfirmation = (confirm) => {
        setShowConfirmation(confirm);
    };

    const handleDeleteItem = () => {
        handleDeleteConfirmation(true);
    };

    const desktopLayout = (
        <Link
            to={`/tapahtumat/${event.id}`}
            style={{
                textDecoration: 'none',
                color: 'black'
            }}
        >
            <ListItem
                divider
                key={event.id}
                sx={{
                    ...itemStyles,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginTop: 1,
                    gap: 5,
                }}
            >
                <ListItemAvatar sx={{ flex: 1 }}>
                    <Avatar
                        src={bannerImage?.src || '/SROS-logo-vihrea.jpg'}
                        imgProps={{ loading: 'lazy', decoding: 'async' }}
                        sx={{ width: 70, height: 70 }}
                    />
                </ListItemAvatar>
                <ListItemText
                    sx={{ flex: 1 }}
                    primary={event.date.day}
                    secondary={event.date.monthName}
                    primaryTypographyProps={{ variant: 'h6' }}
                    secondaryTypographyProps={{ variant: 'overline', fontSize: '0.8rem' }}
                />
                <ListItemText
                    sx={{ flex: 7 }}
                    primary={
                        <React.Fragment>
                            <span style={{ display: 'block', marginBottom: '5px' }}>{event.nimi}</span>
                            <span style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.5)' }}>
                                {(event?.paikka?.nimi || '').trim()}, {event?.paikka?.kunta}, <Chip
                                    label={event.status}
                                    color={
                                        event.status === 'julkaistu' ? 'success' :
                                            ['laskutuksessa', 'laskutettu'].includes(event.status) ? 'warning' : 'primary'
                                    }
                                    sx={{ ml: 1 }}
                                />
                            </span>
                        </React.Fragment>
                    }
                    primaryTypographyProps={{ variant: 'h5', fontSize: '1rem', letterSpacing: '1px' }}
                />
                <div style={{ flex: 1 }}>
                    {(user.role === 'Admin' || user.role === 'Suunnittelija' || user.role === 'Muokkaaja') && (
                        <IconButton disabled={copying} onClick={async (e) => { e.preventDefault(); await handleCopy(); }}>
                            <ContentCopyIcon />
                        </IconButton>
                    )}
                    {(user.role === 'Admin' || user.role === 'Muokkaaja' || (user.role === 'Suunnittelija' && user.userId === event.user)) && (
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteItem();
                            }}
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </div>
            </ListItem>
        </Link>
    );

    const mobileLayout = (
        <Link to={`/tapahtumat/${event.id}`} style={{ textDecoration: 'none', color: 'black' }}>
            <ListItem
                divider
                key={event.id}
                sx={{
                    ...itemStyles,
                    textTransform: 'capitalize',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    py: 2,
                    px: 2,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                    <ListItemAvatar>
                        <Avatar
                            src={bannerImage?.src || '/SROS-logo-vihrea.jpg'}
                            imgProps={{ loading: 'lazy', decoding: 'async' }}
                            sx={{ width: 60, height: 60 }}
                        />
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                        <ListItemText
                            primary={`${event.date.day} ${event.date.monthName}`}
                            primaryTypographyProps={{ variant: 'h6', fontSize: '1rem' }}
                        />
                    </Box>
                </Box>
                <Box sx={{ width: '100%' }}>
                    <ListItemText
                        primary={event.nimi}
                        primaryTypographyProps={{ variant: 'h5', fontSize: '1rem', letterSpacing: '1px' }}
                    />
                    {event?.paikka?.nimi && (
                        <ListItemText
                            primary={`${event?.paikka?.nimi}, ${event?.paikka?.kunta}`}
                            primaryTypographyProps={{
                                variant: 'body2',
                                fontSize: '0.8rem',
                                color: 'rgba(0, 0, 0, 0.5)',
                            }}
                        />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Chip
                            label={event.status}
                            color={
                                event.status === 'julkaistu' ? 'success' :
                                    ['laskutuksessa', 'laskutettu'].includes(event.status) ? 'warning' : 'primary'
                            }
                            size="small"
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {(user.role === 'Admin' || user.role === 'Suunnittelija' || user.role === 'Muokkaaja') && (
                                <IconButton disabled={copying} onClick={async (e) => { e.preventDefault(); await handleCopy(); }} size="small">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            )}
                            {(user.role === 'Admin' || user.role === 'Muokkaaja' || (user.role === 'Suunnittelija' && user.userId === event.user)) && (
                                <IconButton
                                    onClick={(e) => { e.preventDefault(); handleDeleteItem(); }}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>
            </ListItem>
        </Link>
    );

    const alerts = (
        <Grid item xs={12} display="flex" justifyContent="center">
            {successMessage && (
                <Box my={2}>
                    <Alert severity="success" onClose={() => setSuccessMessage('')}>
                        {successMessage}
                    </Alert>
                </Box>
            )}
            {errorMessage && (
                <Box my={2}>
                    <Alert severity="error" onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                </Box>
            )}
            {showConfirmation && (
                <Box my={2}>
                    <Alert severity="warning">
                        Oletko varma että haluat poistaa tapahtuman?
                        <Button
                            color="inherit"
                            onClick={async (e) => {
                                e.preventDefault();
                                await deleteEvent(event);
                                handleSuccess('Tapahtuma poistettu onnistuneesti!');
                                setShowConfirmation(false);
                            }}
                        >
                            Kyllä
                        </Button>
                        <Button color="inherit" onClick={() => setShowConfirmation(false)}>
                            Ei
                        </Button>
                    </Alert>
                </Box>
            )}
        </Grid>
    );

    return (
        <>
            {screenIsBiggerThan600px ? desktopLayout : mobileLayout}
            {alerts}
        </>
    );
};

EventListItem.propTypes = {
    event: PropTypes.object,
    events: PropTypes.array,
    deleteEvent: PropTypes.func,
};

export default React.memo(EventListItem);